import { isAxiosError } from "axios";

import { ConfirmDialog, ImageTile, notify } from "@/components/kit";
import { useDisassociateScrapedProduct } from "@/hooks/api/scraped-product/useDisassociateScrapedProduct";
import { ScrapedProduct } from "@/types/scraped-product";

interface DisassociateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  scrapedProducts: ScrapedProduct[];
}

export const DisassociateProductModal = ({
  isOpen,
  onClose,
  scrapedProducts,
}: DisassociateProductModalProps) => {
  const { mutate, isPending } = useDisassociateScrapedProduct();

  const handleConfirm = () => {
    mutate(
      scrapedProducts.map((p) => p.id),
      {
        onSuccess: () => {
          notify.success(
            "Products Disassociated",
            `Successfully unlinked ${scrapedProducts.length} scraped product${scrapedProducts.length > 1 ? "s" : ""} from catalog product`,
          );
          onClose();
        },
        onError: (error) => {
          let message = "Please try again and contact support";

          if (isAxiosError(error)) {
            message = error.response?.data?.message || message;
          }

          notify.error("Disassociation Failed", message);
        },
      },
    );
  };

  const many = scrapedProducts.length > 1;

  return (
    <ConfirmDialog
      confirmLabel="Unlink"
      isOpen={isOpen}
      isPending={isPending}
      title={
        many ? `Unlink ${scrapedProducts.length} products?` : "Unlink product?"
      }
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-4">
        <p>
          {many
            ? "These scraped products will no longer be linked to their catalog products. You can link them again later."
            : "This scraped product will no longer be linked to its catalog product. You can link it again later."}
        </p>
        <ul className="flex max-h-56 flex-col gap-3 overflow-y-auto">
          {scrapedProducts.map((product) => (
            <li key={product.id} className="flex items-center gap-3">
              <ImageTile src={product.images?.[0]} />
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate font-medium">{product.name}</span>
                <span className="text-muted truncate text-xs">
                  {product.supplier.name}
                  {product.product ? (
                    <>
                      {" "}
                      · Linked to{" "}
                      <span className="text-(--foreground)">
                        {product.product.name}
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </ConfirmDialog>
  );
};
